import { Element as SvgElement } from '@svgdotjs/svg.js';
import { Point } from 'core/geometry';
import { Utils } from 'core/utils';

export interface CropGestureHandler
{
  beginGesture(node: Element): boolean;
  moveBy(delta: Point, center: Point, factor: number): void;
  resizeBy(offset: Point): void;
  endGesture(): void;
}

enum GestureMode
{
  Pan,
  Resize,
  Pinch,
}

interface PinchState
{
  distance: number;
  center: Point;
}

function positionOf(event: MouseEvent): Point
{
  return new Point(event.clientX, event.clientY);
}

export class CropGestureSensor
{
  private static readonly WHEEL_ZOOM_STEP: number = 1.12;
  private static readonly WHEEL_NOTCH: number = 200; /* px */
  private static readonly WHEEL_SETTLE_DELAY: number = 100; /* ms */
  private static readonly MIN_PINCH_DISTANCE: number = 8; /* px */
  private static readonly MAX_ZOOM_STEP: number = 1.5;

  private readonly node: Element;
  private readonly listeners: Object = {};

  private readonly pointers: Map<number, Point> = new Map();

  private mode: GestureMode = undefined;
  private origin: Point = undefined;
  private pinch: PinchState = undefined;

  private gesturePointer: number = undefined;
  private wheelSettleHandle: any = undefined;

  constructor(element: SvgElement, private readonly handler: CropGestureHandler)
  {
    this.node = element.node;

    this.listeners['pointerdown'] = this.onPointerDown.bind(this);
    this.listeners['pointermove'] = this.onPointerMove.bind(this);
    this.listeners['pointerup'] = this.onPointerUp.bind(this);
    this.listeners['pointercancel'] = this.onPointerUp.bind(this);
    this.listeners['wheel'] = this.onWheel.bind(this);
    this.listeners['touchstart'] = this.onTouchStart.bind(this);

    for (const type in this.listeners) {
      if (this.listeners.hasOwnProperty(type)) {
        this.node.addEventListener(
            type, this.listeners[type], {passive: false});
      }
    }
  }

  public destructor(): void
  {
    for (const type in this.listeners) {
      if (this.listeners.hasOwnProperty(type)) {
        this.node.removeEventListener(type, this.listeners[type]);
      }
    }

    if (this.wheelSettleHandle !== undefined) {
      clearTimeout(this.wheelSettleHandle);
    }

    if (this.pointers.size > 0) {
      Utils.ChangeDetection.resume();
    }
  }

  private onTouchStart(event: TouchEvent): void
  {
    event.preventDefault();
  }

  private onPointerDown(event: PointerEvent): void
  {
    event.preventDefault();
    this.capture(event.pointerId);
    this.pointers.set(event.pointerId, positionOf(event));

    if (this.pointers.size === 1) {
      Utils.ChangeDetection.pause();

      this.origin = positionOf(event);
      this.gesturePointer = event.pointerId;
      this.mode =
          this.handler.beginGesture(event.target as Element)
              ? GestureMode.Resize
              : GestureMode.Pan;

      return;
    }

    if (this.mode !== GestureMode.Resize) {
      this.mode = GestureMode.Pinch;
      this.pinch = this.pinchState();
    }
  }

  private onPointerMove(event: PointerEvent): void
  {
    const previous = this.pointers.get(event.pointerId);
    if (previous === undefined) {
      return;
    }

    const current = positionOf(event);
    this.pointers.set(event.pointerId, current);

    switch (this.mode) {
      case GestureMode.Pinch:
        this.applyPinch();
        break;

      case GestureMode.Resize:
        if (event.pointerId === this.gesturePointer) {
          this.handler.resizeBy(
              new Point(
                  current.x - this.origin.x, current.y - this.origin.y));
        }
        break;

      case GestureMode.Pan:
        this.handler.moveBy(
            new Point(current.x - previous.x, current.y - previous.y),
            this.toViewportSpace(current),
            1);
        break;

      default:
        break;
    }
  }

  private onPointerUp(event: PointerEvent): void
  {
    if (! this.pointers.delete(event.pointerId)) {
      return;
    }

    if (this.pointers.size === 0) {
      Utils.ChangeDetection.resume();
    }

    if (this.mode === undefined) {
      return;
    }

    if (this.pointers.size === 0
        || (this.mode === GestureMode.Resize
            && event.pointerId === this.gesturePointer))
    {
      this.mode = undefined;
      this.pinch = undefined;
      this.gesturePointer = undefined;
      this.handler.endGesture();
      return;
    }

    if (this.mode === GestureMode.Resize) {
      return;
    }

    this.mode =
        this.pointers.size === 1 ? GestureMode.Pan : GestureMode.Pinch;
    this.pinch = this.pointers.size === 1 ? undefined : this.pinchState();
  }

  private onWheel(event: WheelEvent): void
  {
    event.preventDefault();

    if (event.deltaY === 0) {
      return;
    }

    this.handler.moveBy(
        new Point(0, 0),
        this.toViewportSpace(positionOf(event)),
        this.stepped(
            Math.pow(
                CropGestureSensor.WHEEL_ZOOM_STEP, -this.notchesOf(event))));

    if (this.wheelSettleHandle !== undefined) {
      clearTimeout(this.wheelSettleHandle);
    }

    this.wheelSettleHandle = setTimeout(
        () => {
          this.wheelSettleHandle = undefined;
          this.handler.endGesture();
        },
        CropGestureSensor.WHEEL_SETTLE_DELAY);
  }

  private applyPinch(): void
  {
    const current = this.pinchState();
    const previous = this.pinch;
    this.pinch = current;

    const tooClose =
        previous.distance < CropGestureSensor.MIN_PINCH_DISTANCE
        || current.distance < CropGestureSensor.MIN_PINCH_DISTANCE;

    this.handler.moveBy(
        new Point(
            current.center.x - previous.center.x,
            current.center.y - previous.center.y),
        this.toViewportSpace(current.center),
        tooClose
            ? 1
            : this.stepped(current.distance / previous.distance));
  }

  private notchesOf(event: WheelEvent): number
  {
    const perLine = 16;  /* px */
    const perPage = 400; /* px */

    const pixels =
        event.deltaMode === event.DOM_DELTA_LINE ? event.deltaY * perLine
      : event.deltaMode === event.DOM_DELTA_PAGE ? event.deltaY * perPage
      : event.deltaY;

    return pixels / CropGestureSensor.WHEEL_NOTCH;
  }

  private stepped(factor: number): number
  {
    const step = CropGestureSensor.MAX_ZOOM_STEP;

    return Math.min(step, Math.max(1 / step, factor));
  }

  private capture(pointerId: number): void
  {
    try {
      this.node.setPointerCapture(pointerId);
    } catch {
    }
  }

  private pinchState(): PinchState
  {
    const [first, second] = Array.from(this.pointers.values());

    return {
      distance: Math.hypot(first.x - second.x, first.y - second.y),
      center: new Point(
          (first.x + second.x) / 2, (first.y + second.y) / 2),
    };
  }

  private toViewportSpace(clientPoint: Point): Point
  {
    const bounds = this.node.getBoundingClientRect();

    return new Point(clientPoint.x - bounds.left, clientPoint.y - bounds.top);
  }
}
